// ===== TypeScript Interfaces =====

export interface SpecHighlight {
  value: string;
  label: string;
}

export interface SpecRow {
  name: string;
  value: string;
}

export interface SpecSection {
  title: string;
  rows: SpecRow[];
}

export interface ProductFeature {
  title: string;
  description: string;
}

export interface HvacProduct {
  id: string;
  name: string;
  tagline: string;
  category: string;
  heroBackground: 'gradient-teal' | 'gradient-dark' | 'gradient-slate' | 'dark';
  description: string;
  image: string;
  highlights: SpecHighlight[];
  specSections: SpecSection[];
  features: ProductFeature[];
  capacityRange?: string;
}

// ===== Product Data =====

export const hvacProducts: HvacProduct[] = [
  {
    id: 'breezein-series',
    name: 'BreezeIN Series',
    tagline: 'Smart Comfort Mini-Split',
    category: 'Single-Zone Mini-Split',
    heroBackground: 'gradient-teal',
    image: process.env.PUBLIC_URL + '/imgs/breezein-cover.png',
    capacityRange: '9K – 24K BTU',
    description:
      'The BreezeIN Series delivers powerful cooling with auto self-cleaning technology and gentle breeze mode for whisper-quiet comfort. AHRI certified and Energy Star rated, this series is ideal for bedrooms, living rooms, and offices.',
    highlights: [
      { value: '24.0', label: 'SEER2' },
      { value: '-13°F', label: 'Min Heating' },
      { value: '24K', label: 'Max BTU' },
      { value: 'R-454b', label: 'Refrigerant' },
    ],
    specSections: [
      {
        title: 'Performance',
        rows: [
          { name: 'Cooling Capacity', value: '9,000 – 24,000 BTU' },
          { name: 'Heating Capacity', value: '9,000 – 24,000 BTU' },
          { name: 'SEER2 Rating', value: 'Up to 24.0' },
          { name: 'HSPF2 Rating', value: 'Up to 10.0' },
          { name: 'Min Heating Temp', value: '-13°F (-25°C)' },
          { name: 'Refrigerant', value: 'R-454b' },
          { name: 'Compressor', value: 'DC Inverter' },
        ],
      },
      {
        title: 'Features',
        rows: [
          { name: 'Auto Self-Cleaning', value: 'Yes' },
          { name: 'Gentle Breeze Mode', value: 'Yes' },
          { name: 'AHRI Certified', value: 'Yes' },
          { name: 'ENERGY STAR', value: 'Certified' },
          { name: 'Wi-Fi Enabled', value: 'Yes' },
        ],
      },
      {
        title: 'Warranty',
        rows: [
          { name: 'Parts', value: '10 Years' },
          { name: 'Compressor', value: '10 Years' },
        ],
      },
    ],
    features: [
      {
        title: 'Auto Self-Cleaning',
        description:
          'Automatic evaporator cleaning keeps the unit fresh and efficient, reducing maintenance and extending system life.',
      },
      {
        title: 'Gentle Breeze Mode',
        description:
          'Soft, diffused airflow prevents uncomfortable drafts while maintaining even temperature distribution throughout the room.',
      },
      {
        title: 'AHRI Certified',
        description:
          'Performance independently verified by the Air-Conditioning, Heating, and Refrigeration Institute for reliable efficiency ratings.',
      },
      {
        title: 'Deep Low-Temp Heating',
        description:
          'Reliable heating performance down to -13°F ensures year-round comfort even in the coldest New England winters.',
      },
    ],
  },
  {
    id: 'ultimate-25-series',
    name: 'Ultimate 25 Series',
    tagline: 'Flagship High-Efficiency Mini-Split',
    category: 'Flagship Mini-Split',
    heroBackground: 'gradient-dark',
    image: process.env.PUBLIC_URL + '/imgs/ultimate25-cover.png',
    capacityRange: '9K – 24K BTU',
    description:
      'The Ultimate 25 Series is our highest-efficiency mini-split, achieving up to 25.0 SEER2 with deep self-cleaning technology. Energy Star Most Efficient 2025 certified, it operates down to -22°F for extreme cold climate performance.',
    highlights: [
      { value: '25.0', label: 'SEER2' },
      { value: '-22°F', label: 'Min Heating' },
      { value: '24K', label: 'Max BTU' },
      { value: 'R-454b', label: 'Refrigerant' },
    ],
    specSections: [
      {
        title: 'Performance',
        rows: [
          { name: 'Cooling Capacity', value: '9,000 – 24,000 BTU' },
          { name: 'Heating Capacity', value: '9,000 – 24,000 BTU' },
          { name: 'SEER2 Rating', value: 'Up to 25.0' },
          { name: 'HSPF2 Rating', value: 'Up to 10.5' },
          { name: 'Min Heating Temp', value: '-22°F (-30°C)' },
          { name: 'Refrigerant', value: 'R-454b' },
          { name: 'Compressor', value: 'DC Inverter' },
        ],
      },
      {
        title: 'Features',
        rows: [
          { name: 'Deep Self-Cleaning', value: 'Yes' },
          { name: 'Energy Star Most Efficient 2025', value: 'Yes' },
          { name: 'AHRI Certified', value: 'Yes' },
          { name: 'Wi-Fi Enabled', value: 'Yes' },
          { name: 'Hyper Heating', value: 'Yes' },
        ],
      },
      {
        title: 'Warranty',
        rows: [
          { name: 'Parts', value: '10 Years' },
          { name: 'Compressor', value: '10 Years' },
        ],
      },
    ],
    features: [
      {
        title: 'Industry-Leading Efficiency',
        description:
          'Up to 25.0 SEER2 delivers maximum energy savings, qualifying for the highest rebates from Mass Save and other utility programs.',
      },
      {
        title: 'Extreme Cold Performance',
        description:
          'Operates down to -22°F with full heating capacity, ensuring reliable comfort through the harshest New England winters.',
      },
      {
        title: 'Deep Self-Cleaning',
        description:
          'Advanced self-cleaning technology thoroughly cleans the evaporator coil, maintaining peak efficiency and indoor air quality.',
      },
      {
        title: 'Energy Star Most Efficient',
        description:
          'Recognized as among the most efficient products on the market by the EPA Energy Star Most Efficient 2025 certification.',
      },
    ],
  },
  {
    id: 'elite-series',
    name: 'Elite Series',
    tagline: 'Single-Zone Smart Comfort',
    category: 'Single-Zone Mini-Split',
    heroBackground: 'gradient-slate',
    image: process.env.PUBLIC_URL + '/imgs/elite-cover.png',
    capacityRange: '36K BTU',
    description:
      'The Elite Series delivers rapid cooling with natural unbounded airflow and iFeel sensor technology for intelligent comfort. Designed for larger single-zone applications where powerful, responsive climate control is essential.',
    highlights: [
      { value: '23.0', label: 'SEER2' },
      { value: '-4°F', label: 'Min Heating' },
      { value: '36K', label: 'BTU' },
      { value: 'R-454b', label: 'Refrigerant' },
    ],
    specSections: [
      {
        title: 'Performance',
        rows: [
          { name: 'Capacity', value: '36,000 BTU' },
          { name: 'SEER2 Rating', value: 'Up to 23.0' },
          { name: 'HSPF2 Rating', value: 'Up to 10.0' },
          { name: 'Min Heating Temp', value: '-4°F (-20°C)' },
          { name: 'Refrigerant', value: 'R-454b' },
          { name: 'Compressor', value: 'DC Inverter' },
        ],
      },
      {
        title: 'Features',
        rows: [
          { name: 'Rapid Cooling', value: 'Yes' },
          { name: 'Natural Unbounded Airflow', value: 'Yes' },
          { name: 'iFeel Sensor', value: 'Yes' },
          { name: 'Wi-Fi Enabled', value: 'Yes' },
          { name: 'ENERGY STAR', value: 'Certified' },
        ],
      },
      {
        title: 'Warranty',
        rows: [
          { name: 'Parts', value: '10 Years' },
          { name: 'Compressor', value: '10 Years' },
        ],
      },
    ],
    features: [
      {
        title: 'Rapid Cooling',
        description:
          'Powerful cooling performance reaches target temperature quickly, even in large rooms or challenging conditions.',
      },
      {
        title: 'Natural Unbounded Airflow',
        description:
          'Advanced vane design creates a natural, comfortable airflow pattern that eliminates cold spots and drafts.',
      },
      {
        title: 'iFeel Temperature Sensor',
        description:
          'Remote sensor detects the temperature at your location, not just at the unit, for more accurate and comfortable climate control.',
      },
      {
        title: 'High-Capacity Single Zone',
        description:
          'At 36,000 BTU, the Elite Series handles large living spaces, open-concept areas, and commercial applications with ease.',
      },
    ],
  },
  {
    id: 'freshin-3-series',
    name: 'FreshIN 3.0 Series',
    tagline: 'Premium Fresh Air Unit',
    category: 'Fresh Air Mini-Split',
    heroBackground: 'gradient-teal',
    image: process.env.PUBLIC_URL + '/imgs/freshin-cover.png',
    capacityRange: '12K BTU',
    description:
      'The FreshIN 3.0 Series is a premium fresh air unit with a liftable fresh air inlet and QuadruPuri four-stage filtration system. Real-time TVOC air quality display ensures you always know the quality of your indoor air.',
    highlights: [
      { value: '24.0', label: 'SEER2' },
      { value: '-4°F', label: 'Min Heating' },
      { value: '12K', label: 'BTU' },
      { value: 'R-32', label: 'Refrigerant' },
    ],
    specSections: [
      {
        title: 'Performance',
        rows: [
          { name: 'Capacity', value: '12,000 BTU' },
          { name: 'SEER2 / EER2', value: '24.0 / 13.0' },
          { name: 'HSPF2 / COP', value: '9.5 / 4.2' },
          { name: 'Min Heating Temp', value: '-4°F (-20°C)' },
          { name: 'Refrigerant', value: 'R-32' },
          { name: 'Compressor', value: 'DC Inverter' },
        ],
      },
      {
        title: 'Air Quality',
        rows: [
          { name: 'Liftable Fresh Air Inlet', value: 'Yes' },
          { name: 'QuadruPuri 4-Stage Filtration', value: 'Yes' },
          { name: 'Real-Time TVOC Display', value: 'Yes' },
          { name: 'PM2.5 Monitoring', value: 'Yes' },
        ],
      },
      {
        title: 'Warranty',
        rows: [
          { name: 'Parts', value: '10 Years' },
          { name: 'Compressor', value: '10 Years' },
        ],
      },
    ],
    features: [
      {
        title: 'Liftable Fresh Air Inlet',
        description:
          'Introduces filtered outdoor air into your space, reducing CO2 levels and maintaining a healthy indoor environment.',
      },
      {
        title: 'QuadruPuri 4-Stage Filtration',
        description:
          'Four stages of air purification remove dust, allergens, bacteria, and odors for the cleanest possible indoor air.',
      },
      {
        title: 'Real-Time Air Quality Display',
        description:
          'Built-in TVOC sensor shows real-time air quality readings so you always know what you\'re breathing.',
      },
      {
        title: 'Premium Efficiency',
        description:
          'Achieves 24.0 SEER2 and 13.0 EER2 for exceptional energy efficiency while maintaining superior air quality.',
      },
    ],
  },
  {
    id: 'advantage-series',
    name: 'Advantage Series',
    tagline: 'Ducted Inverter Heat Pump',
    category: 'Ducted Heat Pump',
    heroBackground: 'gradient-dark',
    image: process.env.PUBLIC_URL + '/imgs/advantage-cover.png',
    capacityRange: '24K – 60K BTU',
    description:
      'The Advantage Series ducted inverter heat pump works with your existing ductwork for whole-home comfort. DC inverter design with 24V thermostat compatibility and RS485 communication for seamless integration into any home.',
    highlights: [
      { value: '19.0', label: 'SEER2' },
      { value: '-5°F', label: 'Min Heating' },
      { value: '60K', label: 'Max BTU' },
      { value: 'R-454b', label: 'Refrigerant' },
    ],
    specSections: [
      {
        title: 'Performance',
        rows: [
          { name: 'Cooling Capacity', value: '24,000 – 60,000 BTU' },
          { name: 'Heating Capacity', value: '24,000 – 60,000 BTU' },
          { name: 'SEER2 Rating', value: 'Up to 19.0' },
          { name: 'HSPF2 Rating', value: 'Up to 9.3' },
          { name: 'Min Heating Temp', value: '-5°F (-20°C)' },
          { name: 'Refrigerant', value: 'R-454b' },
          { name: 'Compressor', value: 'DC Inverter' },
        ],
      },
      {
        title: 'Integration',
        rows: [
          { name: '24V Thermostat Compatible', value: 'Yes' },
          { name: 'RS485 Communication', value: 'Yes' },
          { name: 'Multi-Protection System', value: 'Yes' },
          { name: 'Works with Existing Ductwork', value: 'Yes' },
        ],
      },
      {
        title: 'Warranty',
        rows: [
          { name: 'Parts', value: '10 Years' },
          { name: 'Compressor', value: '10 Years' },
        ],
      },
    ],
    features: [
      {
        title: 'Whole-Home Ducted Solution',
        description:
          'Connects to your existing ductwork for even heating and cooling throughout every room in your home.',
      },
      {
        title: 'DC Inverter Efficiency',
        description:
          'Variable-speed compressor adjusts output precisely, reducing energy consumption and maintaining consistent comfort.',
      },
      {
        title: '24V Thermostat Compatible',
        description:
          'Works with standard 24V thermostats and popular smart thermostats for easy installation and familiar controls.',
      },
      {
        title: 'RS485 Communication',
        description:
          'Advanced communication protocol enables integration with building management systems and smart home platforms.',
      },
    ],
  },
  {
    id: 'multi-match',
    name: 'Multi Match Series',
    tagline: 'Multi-Zone Flexible System',
    category: 'Multi-Zone System',
    heroBackground: 'gradient-slate',
    image: process.env.PUBLIC_URL + '/imgs/multimatch-cover.png',
    capacityRange: '18K – 42K BTU',
    description:
      'The Multi Match Series provides full DC inverter multi-zone comfort with smart defrosting and AHRI-certified performance. Mix and match wall mount, ceiling & floor, slim duct, and cassette indoor units on a single outdoor unit.',
    highlights: [
      { value: 'Multi', label: 'Zone' },
      { value: '-13°F', label: 'Min Heating' },
      { value: '42K', label: 'Max BTU' },
      { value: 'R-454b', label: 'Refrigerant' },
    ],
    specSections: [
      {
        title: 'Performance',
        rows: [
          { name: 'Capacity Range', value: '18,000 – 42,000 BTU' },
          { name: 'Cooling Ambient Range', value: '5°F – 133°F' },
          { name: 'Heating Ambient Range', value: '-13°F – 86°F' },
          { name: 'Refrigerant', value: 'R-454b' },
          { name: 'Compressor', value: 'Full DC Inverter' },
        ],
      },
      {
        title: 'Configuration',
        rows: [
          { name: '2-Zone (18K BTU)', value: '2 indoor units' },
          { name: '3-Zone (27K BTU)', value: '3 indoor units' },
          { name: '4-Zone (36K BTU)', value: '4 indoor units' },
          { name: '5-Zone (42K BTU)', value: '5 indoor units' },
        ],
      },
      {
        title: 'Indoor Unit Types',
        rows: [
          { name: 'Wall Mount', value: '9K – 24K BTU' },
          { name: 'Ceiling & Floor', value: '9K – 18K BTU' },
          { name: 'Slim Duct', value: '9K – 24K BTU' },
          { name: 'Cassette', value: '9K – 18K BTU' },
        ],
      },
      {
        title: 'Warranty',
        rows: [
          { name: 'Parts', value: '10 Years' },
          { name: 'Compressor', value: '10 Years' },
        ],
      },
    ],
    features: [
      {
        title: 'Full DC Inverter',
        description:
          'Both compressor and fan motors are DC inverter-driven for maximum efficiency and precise zone-by-zone temperature control.',
      },
      {
        title: 'Smart Defrosting',
        description:
          'Intelligent defrost algorithm minimizes defrost cycles and maintains comfort during cold weather operation.',
      },
      {
        title: 'Mix & Match Indoor Units',
        description:
          'Choose wall mount, ceiling & floor, slim duct, or cassette units for each zone — all on a single outdoor unit.',
      },
      {
        title: 'AHRI Certified',
        description:
          'All configurations are independently tested and certified by AHRI for verified performance ratings you can trust.',
      },
    ],
  },
];

// ===== Accessories Data =====

export interface Accessory {
  id: string;
  name: string;
  category: string;
  description: string;
  specs: { label: string; value: string }[];
}

export const accessories: Accessory[] = [
  {
    id: 'mounting-brackets',
    name: 'Mounting Brackets & Bases',
    category: 'Installation',
    description:
      'Heavy-duty galvanized steel brackets and bases for secure outdoor unit installation on walls, roofs, and ground surfaces.',
    specs: [
      { label: 'Material', value: 'Galvanized Steel' },
      { label: 'Max Load', value: 'Up to 400 lbs' },
      { label: 'Finish', value: 'Powder Coated' },
      { label: 'Types', value: 'Wall, Roof, Ground' },
    ],
  },
  {
    id: 'copper-line-sets',
    name: 'Copper Line Sets & Fittings',
    category: 'Refrigeration',
    description:
      'ACR-grade copper tubing with pre-insulated line sets for efficient refrigerant delivery between indoor and outdoor units.',
    specs: [
      { label: 'Grade', value: 'ACR Copper' },
      { label: 'Sizes', value: '1/4″ – 5/8″ OD' },
      { label: 'Insulation', value: 'Pre-Insulated' },
      { label: 'Lengths', value: '15 ft, 25 ft, Custom' },
    ],
  },
  {
    id: 'lineset-covers',
    name: 'Lineset Covers & Raceways',
    category: 'Finishing',
    description:
      'UV-resistant PVC covers that protect and conceal refrigerant lines for a clean, professional exterior installation finish.',
    specs: [
      { label: 'Material', value: 'UV-Resistant PVC' },
      { label: 'Widths', value: '2.5″, 3.5″, 4.5″' },
      { label: 'Finish', value: 'White / Paintable' },
      { label: 'Compatibility', value: 'All TCL Series' },
    ],
  },
];

// ===== Helper =====

export function getProductBySlug(slug: string): HvacProduct | undefined {
  return hvacProducts.find((p) => p.id === slug);
}
